import { MimeType } from '../../../../../common/media/mimeConstants';
import { loadHtmlImageFromBlob } from '../content/utils/imageModalImageBlobUtils';

/**
 * Background removal is the only image flow that sends full image bytes through our own web server:
 * saves upload straight to S3 with a presigned URL, while this request is proxied to the provider.
 * The app server's nginx caps request bodies at 10MB, so anything larger is rejected with a 413
 * before it reaches the route. Stay under that with headroom for the multipart envelope.
 */
export const MAX_BACKGROUND_REMOVAL_UPLOAD_BYTES = 8 * 1024 * 1024;

// Matches the export quality already used elsewhere in the image editor flow.
const RECOMPRESSED_UPLOAD_QUALITY = 0.85;

/**
 * Recompress an oversized source image so it fits within the request body limit.
 *
 * Pixel dimensions are deliberately left alone: the provider result is conformed back onto the source's
 * grid, so sending a smaller image would mean upscaling the returned cutout and softening exactly the
 * edges background removal is judged on. Re-encoding at full size is enough on its own, because the
 * images that breach the limit are overwhelmingly lossless PNGs of photographic content.
 *
 * WebP rather than JPEG because the source may already carry transparency, which JPEG would flatten to
 * black and leave the provider trying to remove a background that wasn't there.
 *
 * This is an optimisation, not a correctness step. Anything that fails here — a format the browser can't
 * decode but the provider can (HEIC), a canvas the browser won't give us — falls back to the original
 * blob and lets the request proceed as it would have before.
 */
export const compressImageBlobForBackgroundRemoval = async (imageBlob: Blob): Promise<Blob> => {
    if (imageBlob.size <= MAX_BACKGROUND_REMOVAL_UPLOAD_BYTES) return imageBlob;

    try {
        const source = await loadHtmlImageFromBlob(imageBlob);

        try {
            const { naturalWidth, naturalHeight } = source.image;
            const canvas = document.createElement('canvas');
            canvas.width = naturalWidth;
            canvas.height = naturalHeight;

            const context = canvas.getContext('2d');
            if (!context) return imageBlob;

            context.drawImage(source.image, 0, 0);

            const compressed = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(resolve, MimeType.WEBP, RECOMPRESSED_UPLOAD_QUALITY);
            });

            // Browsers that can't encode WebP silently fall back to PNG, which won't be any smaller.
            if (!compressed || compressed.type !== MimeType.WEBP) return imageBlob;

            return compressed.size < imageBlob.size ? compressed : imageBlob;
        } finally {
            source.revoke();
        }
    } catch {
        return imageBlob;
    }
};
